import Vue from 'vue'
import axios from 'axios'
import {
  list,
  show,
  create,
  update,
  remove,
  link,
  unlink,
  listServiceProviderCustomer,
  listServiceProviderUsers,
  listServiceProviderCustomers,
  showServiceProviderUser,
  createServiceProviderUser,
  updateServiceProviderUser,
  removeServiceProviderUser,
  createServiceProviderContract,
  createServiceProviderRealEstate,
  updateServiceProviderRealEstate,
  deleteServiceProviderRealEstate,
  showServiceProviderContractByRealEstate,
  listServiceProviderContractServiceTypes,
  listServiceProviderUsersFiltered,
  addServiceProviderBalance,
  getServiceProviderLogoUploadUrl,
  addServiceProviderLogo,
  getRealEstateLogoUploadUrl,
  addRealEstateLogo,
} from './serviceProviderService'

/**
 * @param {Object} context - Contexto do Vuex (commit, dispatch)
 * @param {Function} operation - Operação assíncrona a ser executada
 * @param {Object} options - Opções adicionais de configuração
 */

function handleError(dispatch, data) {
  if (!dispatch) return

  const errorMessage = data?.errors?.length
    ? data.errors[0].message
    : data?.message || 'Erro desconhecido'

  dispatch('showErrorMessage', errorMessage)
}

async function executeAction({ commit, dispatch }, loadingType, operation, options = {}) {
  const loadingMutation = loadingType

  commit(loadingMutation, true)

  try {
    const result = await operation()

    if (options.refreshAfter) dispatch('listServiceProviders')
    if (options.refreshServiceProviderUsers) dispatch('listServiceProviderUsers')
    if (options.refreshServiceProviderCustomers) dispatch('listServiceProviderCustomers')
    if (options.refreshLinkedCustomers) dispatch('listLinkedCustomers')
    if (options.closeModalsAfter) commit('closeAllModals')
    if (options.successMessage) dispatch('showSuccessMessage', options.successMessage)

    return result
  } catch (error) {
    handleError(dispatch, error?.data)
    return Promise.reject(error)
  } finally {
    commit(loadingMutation, false)
  }
}

const state = {
  serviceProviders: [],
  serviceProvider: {},
  serviceProviderUsers: [],
  serviceProviderUser: {},
  isServiceProviderLoading: false,
  linkedCustomers: [],
  serviceProviderCustomers: [],
  serviceProviderContract: null,
  serviceProviderContractError: null,
  selectedRealEstate: null,
  serviceProviderContractServiceTypes: [],
  serviceProviderContractServiceTypesLoading: false,
  serviceProviderContractServiceTypesError: null,
  serviceProviderUsersForAppointment: [],
  serviceProviderUsersForAppointmentLoading: false,
  isServiceProviderLogoUploading: false,
  isRealEstateLogoUploading: false,
}

const getters = {
  serviceProviders: state => state.serviceProviders,
  serviceProvider: state => state.serviceProvider,
  serviceProviderUsers: state => state.serviceProviderUsers,
  serviceProviderUser: state => state.serviceProviderUser,
  isServiceProviderLoading: state => state.isServiceProviderLoading,
  linkedCustomers: state => state.linkedCustomers,
  serviceProviderCustomers: state => state.serviceProviderCustomers,
  serviceProviderContract: state => state.serviceProviderContract,
  serviceProviderContractError: state => state.serviceProviderContractError,
  serviceProviderContractServiceTypes: state => state.serviceProviderContractServiceTypes,
  serviceProviderContractServiceTypesLoading: state => state.serviceProviderContractServiceTypesLoading,
  serviceProviderContractServiceTypesError: state => state.serviceProviderContractServiceTypesError,
  serviceProviderUsersForAppointment: state => state.serviceProviderUsersForAppointment,
  serviceProviderUsersForAppointmentLoading: state => state.serviceProviderUsersForAppointmentLoading,
  isServiceProviderLogoUploading: state => state.isServiceProviderLogoUploading,
  isRealEstateLogoUploading: state => state.isRealEstateLogoUploading,
}

const actions = {
  async listServiceProviders({ commit, dispatch, rootState }, params) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      const defaultParams = {
        size: rootState.paginate.itemsPerPage,
        page: rootState.paginate.currentPage - 1,
      }

      const filters = {
        ...rootState.filters.filters,
        ...params,
      }

      if (filters.search && typeof filters.search === 'string') {
        const isCnpjOrCpf = /^[\d.\-/]+$/.test(filters.search)
        if (isCnpjOrCpf) {
          filters.search = filters.search.replace(/[^\d]/g, '')
        }
      }

      const mergedParams = {
        ...defaultParams,
        ...filters,
      }

      const response = await list(mergedParams)
      const { result, total } = response.data

      commit('setServiceProviders', result)
      commit('setTotalPages', total)
      return response
    })
  },

  async showServiceProvider({ commit, dispatch }, id) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      const response = await show(id)
      commit('setServiceProvider', response.data)
      return response
    })
  },

  async createServiceProvider({ commit, dispatch }, body) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      return await create(body)
    }, { refreshAfter: true, closeModalsAfter: true, successMessage: Vue.i18n.translate('serviceProviders.success.create') })
  },

  async updateServiceProvider({ commit, dispatch }, body) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      return await update(body)
    }, { refreshAfter: true, closeModalsAfter: true, successMessage: Vue.i18n.translate('serviceProviders.success.update') })
  },

  async removeServiceProvider({ commit, dispatch }, id) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      return await remove(id)
    }, { refreshAfter: true, closeModalsAfter: true, successMessage: Vue.i18n.translate('serviceProviders.success.delete') })
  },

  async updateServiceProviderRealEstate({ commit, dispatch }, body) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      const token = localStorage.getItem('access_token')
      const response = await updateServiceProviderRealEstate(body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      return response.data
    }, {
      refreshServiceProviderCustomers: true,
      closeModalsAfter: true,
      successMessage: Vue.i18n.translate('serviceProviders.success.update')
    })
  },

  handleOpenCreateUpdateServiceProviderModal({ dispatch, commit }, serviceProviderId) {
    commit('setServiceProvider', {})
    dispatch('openModal', 'CREATE_UPDATE_SERVICE_PROVIDER_MODAL')
    serviceProviderId && dispatch('showServiceProvider', serviceProviderId)
  },

  handleOpenDeleteServiceProviderModal({ dispatch, commit }, serviceProviderId) {
    commit('setServiceProvider', {})
    dispatch('openModal', 'DELETE_SERVICE_PROVIDER_MODAL')
    serviceProviderId && dispatch('showServiceProvider', serviceProviderId)
  },

  async linkServiceProviderCustomer({ commit, dispatch }, body) {
    return executeAction({ commit, dispatch }, 'setIsLoadingBoard', async () => {
      return await link(body)
    }, { refreshAfter: true, closeModalsAfter: true, successMessage: Vue.i18n.translate('serviceProviders.success.linked'), refreshLinkedCustomers: true })
  },

  async unlinkServiceProviderCustomer({ commit, dispatch }, body) {
    return executeAction({ commit, dispatch }, 'setIsLoadingBoard', async () => {
      return await unlink(body)
    }, { refreshAfter: true, closeModalsAfter: true, successMessage: Vue.i18n.translate('serviceProviders.success.unlinked'), refreshLinkedCustomers: true })
  },

  async listLinkedCustomers({ commit, dispatch, rootState }) {
    return executeAction({ commit, dispatch }, 'setIsLoadingBoard', async () => {
      const defaultParams = {
        size: rootState.paginate.itemsPerPage,
        page: rootState.paginate.currentPage - 1,
      }

      const params = { ...defaultParams, ...rootState.filters.filters }

      const response = await listServiceProviderCustomer(params)
      const { result, total } = response.data

      commit('setLinkedCustomers', result)
      commit('setTotalPages', total)
      return response
    }, { refreshAfter: false, closeModalsAfter: false, })
  },

  // service provider user
  async listServiceProviderUsers({ commit, dispatch, rootState }, userParams) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      const defaultParams = {
        size: rootState.paginate.itemsPerPage,
        page: rootState.paginate.currentPage - 1,
      }

      const params = { ...defaultParams, ...rootState.filters.filters, ...userParams }

      const response = await listServiceProviderUsers(params)
      const { result, total } = response.data

      commit('setServiceProviderUsers', result)
      commit('setTotalPages', total)
      return response
    })
  },

  async showServiceProviderUser({ commit, dispatch }, id) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      const response = await showServiceProviderUser(id)
      commit('setServiceProviderUser', response.data)
      return response
    })
  },

  async createServiceProviderUser({ commit, dispatch }, body) {
    return executeAction(
      { commit, dispatch },
      'setIsServiceProviderLoading',
      async () => createServiceProviderUser(body),
      {
        closeModalsAfter: true,
        successMessage: Vue.i18n.translate('serviceProviders.success.create'),
        refreshServiceProviderUsers: true,
      }
    )
  },

  async updateServiceProviderUser({ commit, dispatch }, body) {
    return executeAction(
      { commit, dispatch },
      'setIsServiceProviderLoading',
      async () => updateServiceProviderUser(body),
      {
        closeModalsAfter: true,
        successMessage: Vue.i18n.translate('serviceProviders.success.update'),
        refreshServiceProviderUsers: true,
      }
    )
  },

  async removeServiceProviderUser({ commit, dispatch }, id) {
    return executeAction(
      { commit, dispatch },
      'setIsServiceProviderLoading',
      async () => removeServiceProviderUser(id),
      {
        closeModalsAfter: true,
        successMessage: Vue.i18n.translate('serviceProviders.success.delete'),
        refreshServiceProviderUsers: true,
      }
    )
  },

  async showServiceProviderContractByRealEstate({ commit, dispatch }, realEstateId) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      commit('setServiceProviderContractError', null)
      try {
        const response = await showServiceProviderContractByRealEstate(realEstateId)
        const data = response.data

        if (data && Object.keys(data).length > 0) {
          commit('setServiceProviderContract', data)
        } else {
          commit('setServiceProviderContract', null)
          commit('setServiceProviderContractError', 'contract.realEstateHasNoActiveContract')
        }

        return response
      } catch (e) {
        commit('setServiceProviderContract', null)
        commit('setServiceProviderContractError', 'contract.realEstateHasNoActiveContract')
        throw e
      }
    })
  },

async deleteServiceProviderRealEstate({ commit, dispatch }, { id, payload = null }) {
  return executeAction(
    { commit, dispatch },
    'setIsServiceProviderLoading',
    async () => {
      const response = await deleteServiceProviderRealEstate(id, payload)
      return response.data
    },
    {
      refreshServiceProviderCustomers: true,
      closeModalsAfter: true,
      successMessage: Vue.i18n.translate('serviceProviders.success.delete'),
    }
  )
},

  async listServiceProviderCustomers({ commit, dispatch, rootState }) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      const defaultParams = {
        size: rootState.paginate.itemsPerPage,
        page: rootState.paginate.currentPage - 1,
      }

      const params = { ...defaultParams, ...rootState.filters.filters }

      const response = await listServiceProviderCustomers(params)
      const { result, total } = response.data

      commit('setServiceProviderCustomers', result)
      commit('setTotalPages', total)
      return response
    })
  },

  async createServiceProviderContract({ commit, dispatch }, body) {
    return executeAction({ commit, dispatch }, 'setIsServiceProviderLoading', async () => {
      const token = localStorage.getItem('access_token');
      const response = await createServiceProviderContract(body, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    }, {
      refreshAfter: false,
      closeModalsAfter: true,
      successMessage: Vue.i18n.translate('contract.creationSuccessMessage')
    });
  },

async createServiceProviderRealEstate({ commit, dispatch }, body) {
  return executeAction(
    { commit, dispatch },
    'setIsServiceProviderLoading',
    async () => {
      const response = await createServiceProviderRealEstate(body)
      return response.data
    },
    {
      refreshServiceProviderCustomers: true,
      closeModalsAfter: true,
      successMessage: Vue.i18n.translate('serviceProviders.success.create'),
    })
  },

  async fetchServiceProviderContractServiceTypes({ commit, dispatch }) {
    commit('setServiceProviderContractServiceTypesLoading', true)
    commit('setServiceProviderContractServiceTypesError', null)

    try {
      const response = await listServiceProviderContractServiceTypes()
      commit('setServiceProviderContractServiceTypes', response.data)
      return response
    } catch (error) {
      commit('setServiceProviderContractServiceTypes', [])
      commit('setServiceProviderContractServiceTypesError', error)
      handleError(dispatch, error?.data)
      return Promise.reject(error)
    } finally {
      commit('setServiceProviderContractServiceTypesLoading', false)
    }
  },

  async searchServiceProviderUsersForAppointment({ commit, dispatch, rootState }, { name, visitType }) {
    return executeAction(
      { commit, dispatch },
      'setServiceProviderUsersForAppointmentLoading',
      async () => {
        const defaultParams = {
          size: rootState.paginate.itemsPerPage,
          page: rootState.paginate.currentPage - 1,
      }

      const filters = {
        ...rootState.filters.filters,
        ...defaultParams,

        name: name || null,
        active: true,
        filterByServiceType: true,
        visitType,
      }

      const response = await listServiceProviderUsersFiltered(filters)

      const items = response.data?.result || response.data?.content || []
      commit('setServiceProviderUsersForAppointment', items)

      return response
    })
  },

  async uploadServiceProviderLogo({ commit, dispatch }, { serviceProviderId, file }) {
    commit('setIsServiceProviderLogoUploading', true)
    try {
      const { data } = await getServiceProviderLogoUploadUrl({
        service_provider_id: serviceProviderId,
        filename: file.name,
        filetype: file.type,
      })
      const signedUrl = data.url
      const externalFilename = data.external_filename
      const externalUrl = signedUrl.substring(0, signedUrl.indexOf('?'))

      await axios.put(signedUrl, file, { headers: { 'Content-Type': file.type } })

      await addServiceProviderLogo({
        service_provider_id: serviceProviderId,
        filename: file.name,
        external_filename: externalFilename,
        external_url: externalUrl,
      })

      await dispatch('showServiceProvider', serviceProviderId)
      dispatch('showSuccessMessage', Vue.i18n.translate('serviceProviders.success.update'))
    } catch (error) {
      handleError(dispatch, error?.data)
    } finally {
      commit('setIsServiceProviderLogoUploading', false)
    }
  },

  async uploadRealEstateLogo({ commit, dispatch }, { realEstateId, file }) {
    commit('setIsRealEstateLogoUploading', true)
    try {
      const { data } = await getRealEstateLogoUploadUrl({
        real_estate_id: realEstateId,
        filename: file.name,
        filetype: file.type,
      })
      const signedUrl = data.url
      const externalFilename = data.external_filename
      const externalUrl = signedUrl.substring(0, signedUrl.indexOf('?'))

      await axios.put(signedUrl, file, { headers: { 'Content-Type': file.type } })

      await addRealEstateLogo({
        real_estate_id: realEstateId,
        filename: file.name,
        external_filename: externalFilename,
        external_url: externalUrl,
      })

      await dispatch('listServiceProviderCustomers')
      dispatch('showSuccessMessage', Vue.i18n.translate('serviceProviders.success.update'))
    } catch (error) {
      handleError(dispatch, error?.data)
    } finally {
      commit('setIsRealEstateLogoUploading', false)
    }
  },

  async serviceProviderAddBalance({ commit, dispatch }, body) {
    return executeAction(
      { commit, dispatch },
      'setIsServiceProviderLoading',
      async () => {
        const response = await addServiceProviderBalance(body)
      return response.data
     },
      {
       refreshServiceProviderCustomers: true,
       closeModalsAfter: true,
      }
    )
  },
}

const mutations = {
  setServiceProviders(state, serviceProviders) {
    state.serviceProviders = serviceProviders
  },

  setServiceProvider(state, serviceProvider) {
    state.serviceProvider = serviceProvider
  },

  setServiceProviderUsers(state, serviceProviderUsers) {
    state.serviceProviderUsers = serviceProviderUsers
  },

  setServiceProviderUser(state, serviceProviderUser) {
    state.serviceProviderUser = serviceProviderUser
  },

  setIsServiceProviderLoading(state, isLoading) {
    state.isServiceProviderLoading = isLoading
  },

  setLinkedCustomers(state, linkedCustomers) {
    state.linkedCustomers = linkedCustomers
  },

  setServiceProviderCustomers(state, serviceProviderCustomers) {
    state.serviceProviderCustomers = serviceProviderCustomers
  },

  setServiceProviderContract(state, contract) {
    state.serviceProviderContract = contract
  },

  setServiceProviderContractError(state, error) {
    state.serviceProviderContractError = error
  },

  setSelectedRealEstate(state, realEstate) {
  state.selectedRealEstate = realEstate
  },

  setServiceProviderContractServiceTypes(state, items) {
  state.serviceProviderContractServiceTypes = items || []
  },

  setServiceProviderContractServiceTypesLoading(state, isLoading) {
  state.serviceProviderContractServiceTypesLoading = isLoading
  },

  setServiceProviderContractServiceTypesError(state, error) {
  state.serviceProviderContractServiceTypesError = error
  },

  setServiceProviderUsersForAppointment(state, items) {
  state.serviceProviderUsersForAppointment = items || []
  },

  setServiceProviderUsersForAppointmentLoading(state, isLoading) {
  state.serviceProviderUsersForAppointmentLoading = isLoading
  },

  setIsServiceProviderLogoUploading(state, isLoading) {
    state.isServiceProviderLogoUploading = isLoading
  },

  setIsRealEstateLogoUploading(state, isLoading) {
    state.isRealEstateLogoUploading = isLoading
  },
}

export default {
  state,
  getters,
  actions,
  mutations,
}
